(() => {
  const CONTROLLER_NAME = 'BuddyMultimediaCarouselController';

  if (window[CONTROLLER_NAME]) {
    window[CONTROLLER_NAME].initAll(document);
    return;
  }

  const instances = new WeakMap();
  const SECTION_SELECTOR = '[data-buddy-multimedia-carousel]';
  const TRACK_SELECTOR = '[data-buddy-carousel-track]';
  const SLIDE_SELECTOR = '[data-buddy-carousel-slide]';
  const MEASURE_SELECTOR = '[data-buddy-carousel-measure]';
  const AUTOPLAY_VISIBILITY_THRESHOLD = 0.5;

  class BuddyMultimediaCarousel {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-buddy-carousel-viewport]');
      this.stage = section.querySelector('.buddy-lp-multimedia-carousel__stage');
      this.track = section.querySelector(TRACK_SELECTOR);
      this.slides = Array.from(section.querySelectorAll(SLIDE_SELECTOR));
      this.hasPeek = section.classList.contains('buddy-lp-multimedia-carousel--has-peek') && this.slides.length > 1;
      this.clones = [];
      this.cloneOffset = 0;
      this.prevButton = section.querySelector('[data-buddy-carousel-prev]');
      this.nextButton = section.querySelector('[data-buddy-carousel-next]');
      this.progress = section.querySelector('[data-buddy-carousel-progress]');
      this.progressCount = section.querySelector('[data-buddy-carousel-count]');
      this.dots = Array.from(section.querySelectorAll('[data-buddy-carousel-dot]'));
      this.index = 0;
      this.timer = null;
      this.startX = 0;
      this.deltaX = 0;
      this.isPointerDown = false;
      this.didSwipe = false;
      this.resizeFrame = null;
      this.isAutoplayVisible = !('IntersectionObserver' in window);
      this.autoplayObserver = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isMotionPaused = this.reducedMotion.matches;

      if (!this.viewport || !this.stage || !this.track || !this.slides.length) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      instances.set(section, this);
      this.createPeekClones();
      this.bind();
      this.goTo(0, false);
      this.startAutoplay();
      section.dataset.buddyMultimediaInitialized = 'true';
    }

    createPeekClones() {
      if (!this.hasPeek) return;

      const firstClone = this.slides[0].cloneNode(true);
      const lastClone = this.slides[this.slides.length - 1].cloneNode(true);

      this.prepareClone(lastClone);
      this.prepareClone(firstClone);
      this.track.insertBefore(lastClone, this.slides[0]);
      this.track.appendChild(firstClone);
      this.clones = [lastClone, firstClone];
      this.cloneOffset = 1;
    }

    prepareClone(clone) {
      clone.setAttribute('data-buddy-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.classList.remove('is-active', 'is-previous', 'is-next');
      clone.removeAttribute('data-slide-index');
      clone.removeAttribute('aria-current');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, video, iframe').forEach((focusable) => {
        focusable.setAttribute('tabindex', '-1');
      });
      clone.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.preload = 'metadata';
      });
    }

    bind() {
      this.handleResize = () => {
        this.syncSocialEmbedWindow();
        this.updateActiveLayout();
      };
      this.handleReducedMotionChange = () => {
        this.isMotionPaused = this.reducedMotion.matches;
        if (this.isMotionPaused) this.pauseAllMedia();
        else {
          this.syncActiveMedia();
          this.startAutoplay();
        }
      };
      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.syncActiveMedia();
          this.startAutoplay();
        }
      };
      this.handlePageShow = () => this.syncActiveMedia();
      this.handleWake = () => this.syncActiveMedia();

      this.prevButton?.addEventListener('click', () => this.previous(), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.next(), { signal: this.signal });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.buddyCarouselDot || 0)), { signal: this.signal });
      });

      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.track.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
      this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal: this.signal });
      this.track.addEventListener('click', (event) => this.onClick(event), { capture: true, signal: this.signal });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.section.querySelectorAll('[data-buddy-carousel-play]').forEach((button) => {
        button.addEventListener('click', (event) => this.playVideo(event), { signal: this.signal });
      });
      this.section.querySelectorAll('[data-buddy-carousel-motion-toggle]').forEach((button) => {
        button.addEventListener('click', (event) => this.toggleVideoMotion(event), { signal: this.signal });
        this.updateMotionButton(button, this.isMotionPaused);
      });

      this.section.querySelectorAll('img').forEach((image) => {
        if (image.complete) this.updateActiveLayout();
        else image.addEventListener('load', () => this.updateActiveLayout(), { once: true, signal: this.signal });
      });

      this.section.querySelectorAll('video').forEach((video) => {
        this.prepareVideo(video);
        video.addEventListener('loadedmetadata', () => {
          this.updateActiveLayout();
          this.syncActiveMedia();
        }, { signal: this.signal });
        video.addEventListener('loadeddata', () => {
          this.updateActiveLayout();
          this.syncActiveMedia();
        }, { signal: this.signal });
        video.addEventListener('canplay', () => this.syncActiveMedia(), { signal: this.signal });
      });

      window.addEventListener('resize', this.handleResize, { signal: this.signal });

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateActiveLayout());
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const wasAutoplayVisible = this.isAutoplayVisible;
            this.isAutoplayVisible = entry.isIntersecting && entry.intersectionRatio >= AUTOPLAY_VISIBILITY_THRESHOLD;

            if (this.isAutoplayVisible && !wasAutoplayVisible) {
              this.startAutoplay();
            } else if (!this.isAutoplayVisible && wasAutoplayVisible) {
              this.stopAutoplay();
            }
          });
        }, { threshold: [0, AUTOPLAY_VISIBILITY_THRESHOLD] });
        this.autoplayObserver.observe(this.section);
      }

      if (this.section.dataset.pauseOnHover === 'true') {
        this.section.addEventListener('mouseenter', () => this.stopAutoplay(), { signal: this.signal });
        this.section.addEventListener('mouseleave', () => this.startAutoplay(), { signal: this.signal });
        this.section.addEventListener('focusin', () => this.stopAutoplay(), { signal: this.signal });
        this.section.addEventListener('focusout', () => this.startAutoplay(), { signal: this.signal });
      }

      this.reducedMotion.addEventListener?.('change', this.handleReducedMotionChange, { signal: this.signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal: this.signal });
      window.addEventListener('pageshow', this.handlePageShow, { signal: this.signal });
      ['pointerdown', 'touchstart', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, this.handleWake, { once: true, passive: true, signal: this.signal });
      });

      if ('IntersectionObserver' in window) {
        this.mediaObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.syncActiveMedia();
          });
        }, { threshold: 0.2 });
        this.mediaObserver.observe(this.section);
      }
    }

    pauseAllMedia() {
      this.stopAutoplay();
      this.section.querySelectorAll('video').forEach((video) => video.pause());
      this.section.querySelectorAll('[data-buddy-carousel-video-wrap] iframe').forEach((iframe) => this.setEmbedPlayback(iframe, false));
      this.section.querySelectorAll('[data-buddy-carousel-motion-toggle]').forEach((button) => this.updateMotionButton(button, true));
    }

    isWrapMotionPaused(wrap) {
      return this.isMotionPaused || wrap?.dataset.buddyMotionPaused === 'true';
    }

    updateMotionButton(button, isPaused) {
      if (!button) return;
      button.setAttribute('aria-pressed', String(isPaused));
      button.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
      button.querySelector('[data-motion-pause-icon]')?.toggleAttribute('hidden', isPaused);
      button.querySelector('[data-motion-play-icon]')?.toggleAttribute('hidden', !isPaused);
    }

    setEmbedPlayback(iframe, shouldPlay) {
      const source = iframe?.getAttribute('src') || '';
      if (!iframe?.contentWindow) return;

      if (source.includes('youtube.com/embed/')) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: shouldPlay ? 'playVideo' : 'pauseVideo', args: [] }), '*');
      } else if (source.includes('player.vimeo.com/')) {
        iframe.contentWindow.postMessage({ method: shouldPlay ? 'play' : 'pause' }, '*');
      } else {
        this.setSocialEmbedAutoplay(iframe, shouldPlay);
      }
    }

    toggleVideoMotion(event) {
      event.preventDefault();
      event.stopPropagation();

      const button = event.currentTarget;
      const wrap = button.closest('[data-buddy-carousel-video-wrap]');
      if (!wrap) return;

      const shouldPlay = this.isWrapMotionPaused(wrap);
      if (shouldPlay) {
        this.isMotionPaused = false;
        wrap.dataset.buddyMotionPaused = 'false';
        this.updateMotionButton(button, false);
        this.syncActiveMedia();
        this.startAutoplay();
      } else {
        wrap.dataset.buddyMotionPaused = 'true';
        wrap.querySelectorAll('video').forEach((video) => video.pause());
        wrap.querySelectorAll('iframe').forEach((iframe) => this.setEmbedPlayback(iframe, false));
        this.updateMotionButton(button, true);
        this.stopAutoplay();
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button')) return;

      this.isPointerDown = true;
      this.didSwipe = false;
      this.startX = event.clientX;
      this.deltaX = 0;
      this.track.setPointerCapture?.(event.pointerId);
      this.stopAutoplay();
    }

    onPointerMove(event) {
      if (!this.isPointerDown) return;
      this.deltaX = event.clientX - this.startX;
    }

    onPointerUp(event) {
      if (!this.isPointerDown) return;
      const threshold = 45;
      const didSwipe = Math.abs(this.deltaX) > threshold;

      if (didSwipe) {
        event?.preventDefault();
        this.didSwipe = true;
        window.setTimeout(() => {
          this.didSwipe = false;
        }, 0);
        if (this.deltaX > 0) this.previous();
        if (this.deltaX < 0) this.next();
      }

      this.isPointerDown = false;
      this.deltaX = 0;
      this.startAutoplay();
    }

    onClick(event) {
      if (!this.didSwipe) return;
      event.preventDefault();
      event.stopPropagation();
      this.didSwipe = false;
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    previous() {
      this.goTo(this.index - 1);
    }

    next() {
      this.goTo(this.index + 1);
    }

    getAutoplayNextIndex() {
      return this.index >= this.slides.length - 1 ? 0 : this.index + 1;
    }

    isSectionMostlyVisible() {
      const rect = this.section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

      return rect.height > 0 && visibleHeight / rect.height >= AUTOPLAY_VISIBILITY_THRESHOLD;
    }

    autoAdvance() {
      if (!this.isAutoplayVisible || document.hidden || !this.isSectionMostlyVisible()) {
        this.isAutoplayVisible = false;
        this.stopAutoplay();
        return;
      }

      this.goTo(this.getAutoplayNextIndex(), false);
    }

    goTo(index, pause = true) {
      if (!this.slides.length) return;
      const maxIndex = this.slides.length - 1;
      this.index = index < 0 ? maxIndex : index > maxIndex ? 0 : index;
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('inert', !isActive);
        slide.querySelectorAll('a, button, video, iframe').forEach((focusable) => {
          if (isActive) focusable.removeAttribute('tabindex');
          else focusable.setAttribute('tabindex', '-1');
        });

        if (!isActive) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('[data-buddy-carousel-video-wrap] iframe').forEach((iframe) => this.setEmbedPlayback(iframe, false));
        }
      });

      this.updateTrackSlideStates();
      this.syncSocialEmbedWindow();
      this.updateProgress();

      this.updateActiveLayout(true);
      this.syncActiveMedia();

      if (pause) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateProgress() {
      const current = this.index + 1;
      const total = this.slides.length;

      if (this.progressCount) this.progressCount.textContent = `Slide ${current} of ${total}`;
      if (this.progress) this.progress.setAttribute('aria-label', `Slide ${current} of ${total}`);
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    getActiveSlide() {
      return this.slides[this.index];
    }

    updateTrackSlideStates() {
      const activeTrackIndex = this.index + this.cloneOffset;

      Array.from(this.track.children).forEach((slide, trackIndex) => {
        slide.classList.toggle('is-previous', trackIndex === activeTrackIndex - 1);
        slide.classList.toggle('is-next', trackIndex === activeTrackIndex + 1);
      });
    }

    prepareVideo(video) {
      video.querySelector('img:not([alt])')?.setAttribute('alt', '');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = this.section.dataset.videoAutoplay !== 'false' && !this.isMotionPaused;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      if (video.autoplay) video.setAttribute('autoplay', '');
      else video.removeAttribute('autoplay');
    }

    syncActiveMedia() {
      const activeSlide = this.getActiveSlide();
      if (!activeSlide) return;

      if (this.section.dataset.videoAutoplay === 'false' || this.isMotionPaused) {
        activeSlide.querySelectorAll('video').forEach((video) => video.pause());
        activeSlide.querySelectorAll('[data-buddy-carousel-video-wrap] iframe').forEach((iframe) => this.setEmbedPlayback(iframe, false));
        activeSlide.querySelectorAll('[data-buddy-carousel-motion-toggle]').forEach((button) => this.updateMotionButton(button, true));
        return;
      }

      activeSlide.querySelectorAll('[data-buddy-carousel-video-wrap]').forEach((wrap) => {
        const isPaused = this.isWrapMotionPaused(wrap);
        const button = wrap.querySelector('[data-buddy-carousel-motion-toggle]');
        this.updateMotionButton(button, isPaused);
        if (isPaused) {
          wrap.querySelectorAll('video').forEach((video) => video.pause());
          wrap.querySelectorAll('iframe').forEach((iframe) => this.setEmbedPlayback(iframe, false));
          return;
        }

        const template = wrap.querySelector('[data-buddy-carousel-embed-template]');
        if (template && !wrap.querySelector('iframe')) {
          wrap.appendChild(template.content.cloneNode(true));
          wrap.querySelector('[data-buddy-carousel-play]')?.classList.add('is-hidden');
        }

        wrap.querySelectorAll('iframe').forEach((iframe) => this.setEmbedPlayback(iframe, true));
      });

      activeSlide.querySelectorAll('video').forEach((video) => {
        if (this.isWrapMotionPaused(video.closest('[data-buddy-carousel-video-wrap]'))) return;
        this.prepareVideo(video);
        const playPromise = video.play();

        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => video.closest('[data-buddy-carousel-video-wrap]')?.querySelector('[data-buddy-carousel-play]')?.classList.add('is-hidden'))
            .catch(() => {});
        }
      });
    }

    setSocialEmbedAutoplay(iframe, shouldAutoplay) {
      const source = iframe?.getAttribute('src');
      if (!source) return;

      try {
        const url = new URL(source, window.location.href);
        const nextValue = shouldAutoplay ? '1' : '0';
        if (url.searchParams.get('autoplay') === nextValue) return;
        url.searchParams.set('autoplay', nextValue);
        iframe.setAttribute('src', url.toString());
      } catch (error) {
        // Leave the original embed URL intact if it cannot be parsed.
      }
    }

    mountSocialEmbedPreview(slide, wrap, template) {
      if (wrap.querySelector('iframe.buddy-lp-multimedia-carousel__social-embed')) return;

      const fragment = template.content.cloneNode(true);
      const iframe = fragment.querySelector('iframe.buddy-lp-multimedia-carousel__social-embed');
      if (!iframe) return;

      iframe.loading = 'lazy';
      const shouldAutoplay = this.section.dataset.videoAutoplay !== 'false' && !this.isWrapMotionPaused(wrap);
      this.setSocialEmbedAutoplay(iframe, shouldAutoplay);

      if (!slide.classList.contains('is-active')) {
        iframe.setAttribute('tabindex', '-1');
      }

      wrap.appendChild(fragment);
      wrap.querySelector('[data-buddy-carousel-play]')?.classList.toggle('is-hidden', shouldAutoplay);
    }

    syncSocialEmbedWindow() {
      if (!this.hasPeek) return;

      const trackSlides = Array.from(this.track.children);
      const activeTrackIndex = this.index + this.cloneOffset;
      const visibleIndexes = new Set([activeTrackIndex]);

      trackSlides.forEach((slide, trackIndex) => {
        slide.querySelectorAll('[data-buddy-carousel-video-wrap]').forEach((wrap) => {
          const template = wrap.querySelector('[data-buddy-carousel-embed-template]');
          if (!template?.content.querySelector('iframe.buddy-lp-multimedia-carousel__social-embed')) return;

          if (visibleIndexes.has(trackIndex)) {
            this.mountSocialEmbedPreview(slide, wrap, template);
          } else {
            wrap.querySelectorAll('iframe.buddy-lp-multimedia-carousel__social-embed').forEach((iframe) => iframe.remove());
          }
        });
      });
    }

    getCarouselMetric(propertyName) {
      const value = parseFloat(window.getComputedStyle(this.section).getPropertyValue(propertyName));
      return Number.isFinite(value) ? value : 0;
    }

    getPeekGutter() {
      if (!this.hasPeek) return 0;
      return this.getCarouselMetric('--buddy-mc-peek-gutter');
    }

    getSlideGap() {
      if (!this.hasPeek) return 0;
      return this.getCarouselMetric('--buddy-mc-slide-gap');
    }

    getInnerContentWidth() {
      const inner = this.section.querySelector('.buddy-lp-multimedia-carousel__inner');
      if (!inner) return 0;

      const styles = window.getComputedStyle(inner);
      const padding = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      return Math.max(0, inner.clientWidth - padding);
    }

    getMobileGutterAllowance() {
      if (!this.hasPeek || window.innerWidth >= 768) return 0;

      const inner = this.section.querySelector('.buddy-lp-multimedia-carousel__inner');
      if (!inner) return 0;

      const styles = window.getComputedStyle(inner);
      const leftPadding = parseFloat(styles.paddingLeft) || 0;
      const rightPadding = parseFloat(styles.paddingRight) || 0;

      return Math.min(leftPadding, 20) + Math.min(rightPadding, 20);
    }

    getStageWidthReserve() {
      if (window.innerWidth >= 1024) return 128;
      if (window.innerWidth >= 768) return 64;
      return this.hasPeek ? 0 : 40;
    }

    getStageAvailableWidth() {
      const contentWidth = this.getInnerContentWidth();
      if (!contentWidth) return 0;

      return Math.max(0, contentWidth + this.getMobileGutterAllowance() - this.getStageWidthReserve());
    }

    getMaxCardWidth() {
      const styles = window.getComputedStyle(this.section);
      const configuredWidth = parseFloat(styles.getPropertyValue('--buddy-mc-card-max-width')) || 700;
      const stageAvailableWidth = this.getStageAvailableWidth() || configuredWidth;
      const mobileCardInset = this.hasPeek && window.innerWidth < 768 ? 24 : 0;
      const peekCount = this.hasPeek && window.innerWidth < 768 ? 1 : 2;
      const peekAdjustedWidth = Math.max(160, stageAvailableWidth - (this.getPeekGutter() * peekCount) - mobileCardInset);
      const availableWidth = this.hasPeek ? peekAdjustedWidth : stageAvailableWidth;
      return Math.min(configuredWidth, availableWidth || configuredWidth);
    }

    getMediaRatio(slide) {
      const media = slide?.querySelector(MEASURE_SELECTOR);
      if (!media) return null;

      const socialVideoRatio = Number(media.dataset.buddySocialVideoRatio);
      if (socialVideoRatio > 0) return socialVideoRatio;

      const image = media.querySelector('img');
      if (image?.naturalWidth && image?.naturalHeight) {
        return image.naturalWidth / image.naturalHeight;
      }

      const video = media.querySelector('video');
      if (video?.videoWidth && video?.videoHeight) {
        return video.videoWidth / video.videoHeight;
      }

      const fallbackRatio = window.getComputedStyle(this.section).getPropertyValue('--buddy-mc-fallback-ratio').trim();
      if (fallbackRatio.includes('/')) {
        const [width, height] = fallbackRatio.split('/').map((value) => Number(value.trim()));
        if (width && height) return width / height;
      }

      return null;
    }

    getResponsiveMediaHeight() {
      const width = window.innerWidth;
      if (width >= 1024) return 500;
      if (width >= 768) return 380;
      return 330;
    }

    isTextOnlyTestimonialSlide(slide) {
      const card = slide?.querySelector('.buddy-lp-multimedia-carousel__card');
      return Boolean(card?.classList.contains('buddy-lp-multimedia-carousel__card--text-only') && (
        card.classList.contains('buddy-lp-multimedia-carousel__card--testimonial_with_rating') ||
        card.classList.contains('buddy-lp-multimedia-carousel__card--testimonial_no_rating')
      ));
    }

    getTextOnlyTestimonialWidth(maxCardWidth) {
      const width = window.innerWidth;
      if (width >= 1024) return Math.min(maxCardWidth, 560);
      if (width >= 768) return Math.min(maxCardWidth, 480);
      return Math.min(maxCardWidth, 280);
    }

    getResponsiveCardWidthCap() {
      const width = window.innerWidth;
      if (width >= 1024) return 640;
      if (width >= 768) return 520;
      return 320;
    }

    getCardWidthForSlide(slide) {
      const maxCardWidth = this.getMaxCardWidth();

      if (this.isTextOnlyTestimonialSlide(slide)) {
        return this.getTextOnlyTestimonialWidth(maxCardWidth);
      }

      const mediaRatio = this.getMediaRatio(slide);
      const responsiveCardWidth = Math.min(maxCardWidth, this.getResponsiveCardWidthCap());

      if (!mediaRatio) {
        return Math.min(responsiveCardWidth, window.innerWidth >= 768 ? 360 : responsiveCardWidth);
      }

      const mediaHeight = this.getResponsiveMediaHeight();
      const widthForNaturalShape = mediaHeight * mediaRatio;
      const minimumWidth = Math.min(responsiveCardWidth, window.innerWidth >= 768 ? 320 : 260);

      if (mediaRatio < 0.9) {
        return Math.max(minimumWidth, Math.min(responsiveCardWidth, widthForNaturalShape));
      }

      return responsiveCardWidth;
    }

    getActiveCardWidth(slide) {
      return this.getCardWidthForSlide(slide);
    }

    updateActiveLayout(immediate = false) {
      window.cancelAnimationFrame(this.resizeFrame);

      if (immediate) {
        this.applyActiveLayout();
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => this.applyActiveLayout());
    }

    applyActiveLayout() {
      const activeSlide = this.getActiveSlide();
      if (!activeSlide) return;

      const cardWidth = this.getActiveCardWidth(activeSlide);
      const mediaRatio = this.getMediaRatio(activeSlide);
      this.stage.style.setProperty('--buddy-mc-active-card-width', `${Math.round(cardWidth)}px`);

      const activeMedia = activeSlide.querySelector(MEASURE_SELECTOR);
      if (activeMedia && mediaRatio) {
        activeMedia.style.setProperty('--buddy-mc-slide-ratio', `${mediaRatio}`);
      }

      const slideWidths = this.updateSlideWidths();
      this.updateViewportHeight(slideWidths);
      this.updateTrackPosition(cardWidth);
    }

    setSlideWidth(slide, width) {
      if (!slide || !width) return;
      slide.style.setProperty('--buddy-mc-slide-width', `${Math.round(width)}px`);
    }

    setSlideMediaRatio(slide, ratio) {
      if (!slide || !ratio) return;
      slide.querySelectorAll(MEASURE_SELECTOR).forEach((media) => {
        media.style.setProperty('--buddy-mc-slide-ratio', `${ratio}`);
      });
    }

    updateSlideWidths() {
      const slideRatios = [];
      const slideWidths = this.slides.map((slide, slideIndex) => {
        const ratio = this.getMediaRatio(slide);
        const width = this.getCardWidthForSlide(slide);

        slideRatios[slideIndex] = ratio;
        this.setSlideMediaRatio(slide, ratio);
        this.setSlideWidth(slide, width);
        return width;
      });

      if (this.hasPeek && this.clones.length === 2 && slideWidths.length) {
        this.setSlideWidth(this.clones[0], slideWidths[slideWidths.length - 1]);
        this.setSlideMediaRatio(this.clones[0], slideRatios[slideRatios.length - 1]);
        this.setSlideWidth(this.clones[1], slideWidths[0]);
        this.setSlideMediaRatio(this.clones[1], slideRatios[0]);
      }

      return slideWidths;
    }

    updateViewportHeight(slideWidths = null) {
      const widths = slideWidths || this.updateSlideWidths();
      const activeSlide = this.getActiveSlide();
      if (!activeSlide) return;

      this.setSlideWidth(activeSlide, widths[this.index]);
      const activeCard = activeSlide.querySelector('.buddy-lp-multimedia-carousel__card');
      const activeHeight = activeCard?.offsetHeight || activeSlide.offsetHeight || 0;

      if (activeHeight) this.viewport.style.height = `${Math.ceil(activeHeight)}px`;
    }

    updateTrackPosition(cardWidth = null) {
      const activeTrackIndex = this.index + this.cloneOffset;
      const activeTrackSlide = this.track.children[activeTrackIndex];
      if (!activeTrackSlide) return;

      // Force the stage width custom property to resolve before reading layout.
      this.stage.offsetWidth;

      const activeCardWidth = cardWidth || activeTrackSlide.offsetWidth || parseFloat(this.stage.style.getPropertyValue('--buddy-mc-active-card-width')) || this.getActiveCardWidth(this.getActiveSlide());
      const useNextPeekOnly = this.hasPeek && window.innerWidth < 768;
      const activeSlideCenter = activeTrackSlide.offsetLeft + (activeCardWidth / 2);
      const viewportCenter = this.viewport.clientWidth / 2;
      const translateX = useNextPeekOnly ? -activeTrackSlide.offsetLeft : viewportCenter - activeSlideCenter;

      this.track.style.transform = `translate3d(${Math.round(translateX)}px, 0, 0)`;
    }

    playVideo(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.currentTarget;
      const wrap = button.closest('[data-buddy-carousel-video-wrap], .buddy-lp-multimedia-carousel__media');
      if (!wrap) return;

      const template = wrap.querySelector('[data-buddy-carousel-embed-template]');
      const video = wrap.querySelector('video');

      if (template && !wrap.querySelector('iframe')) {
        wrap.appendChild(template.content.cloneNode(true));
        this.updateActiveLayout();
      }

      const socialIframe = wrap.querySelector('iframe.buddy-lp-multimedia-carousel__social-embed');
      if (socialIframe) this.setSocialEmbedAutoplay(socialIframe, true);

      if (video) {
        video.controls = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }

      button.classList.add('is-hidden');
      this.stopAutoplay();
    }

    startAutoplay() {
      if (this.section.dataset.autoplay !== 'true' || this.slides.length < 2) return;
      if (this.isMotionPaused) return;
      if (this.getActiveSlide()?.querySelector('[data-buddy-carousel-video-wrap][data-buddy-motion-paused="true"]')) return;
      if (!this.isAutoplayVisible || document.hidden) return;
      if (this.section.dataset.pauseOnHover === 'true' && (
        this.section.matches(':hover') || this.section.contains(document.activeElement)
      )) return;
      this.stopAutoplay();
      const speed = Number(this.section.dataset.autoplaySpeed) || 5000;
      this.timer = window.setInterval(() => this.autoAdvance(), speed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stopAutoplay();
      this.abortController.abort();
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.mediaObserver?.disconnect();
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('pageshow', this.handlePageShow);
      ['pointerdown', 'touchstart', 'scroll'].forEach((eventName) => {
        window.removeEventListener(eventName, this.handleWake);
      });
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.reducedMotion.removeEventListener?.('change', this.handleReducedMotionChange);
      this.section.querySelectorAll('video').forEach((video) => video.pause());
      this.clones.forEach((clone) => clone.remove());
      this.track.style.removeProperty('transform');
      this.viewport.style.removeProperty('height');
      this.stage.style.removeProperty('--buddy-mc-active-card-width');
      instances.delete(this.section);
      delete this.section.dataset.buddyMultimediaInitialized;
    }
  }

  let initializationObserver = null;

  const initSection = (section) => {
    if (!section) return null;
    initializationObserver?.unobserve(section);
    if (instances.has(section)) return instances.get(section);
    return new BuddyMultimediaCarousel(section);
  };

  const destroyAll = (container) => {
    const sections = [];
    if (container instanceof Element && container.matches(SECTION_SELECTOR)) sections.push(container);
    container.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => sections.push(section));
    sections.forEach((section) => {
      initializationObserver?.unobserve(section);
      instances.get(section)?.destroy();
    });
  };

  if ('IntersectionObserver' in window && !(window.Shopify && window.Shopify.designMode)) {
    initializationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        initSection(entry.target);
      });
    }, { rootMargin: '800px 0px' });
  }

  const queueSectionInit = (section) => {
    if (!section || instances.has(section)) return;

    if (initializationObserver) {
      initializationObserver.observe(section);
    } else {
      initSection(section);
    }
  };

  const initAll = (container = document) => {
    if (container instanceof Element && container.matches(SECTION_SELECTOR)) queueSectionInit(container);
    container.querySelectorAll(SECTION_SELECTOR).forEach(queueSectionInit);
  };

  window[CONTROLLER_NAME] = { initAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest(SECTION_SELECTOR);
    if (!section) return;
    const carousel = initSection(section);
    const selectedSlide = event.target.closest(SLIDE_SELECTOR);
    if (selectedSlide) carousel?.goTo(Number(selectedSlide.dataset.slideIndex || 0), false);
  });
})();
